import {isEmptyOrNil, notEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';
import dataLayer from '@upi/data-layer';
import CustomerService from '@services/Customer';
import CartService from '@services/CartService';
import notifier from '@modules/ui/Notifier';
import {getCheckoutPaymentKey, readCheckoutPayment, saveCheckoutPayment} from '@util/CheckoutPaymentStorage';
import {
    shouldRequirePaymentValidation,
    shouldRequirePONumber
} from '@containers/Checkout/salesChannel';
import {getPromotionCode} from '@modules/cart/Promotion';

const promotionCodeMatches = ( promotions,
                               promotionCode ) => promotions?.some( promotion => getPromotionCode( promotion )
    .toUpperCase() === getPromotionCode( promotionCode ).toUpperCase() );

export const ORDER_STATUSES = {
    pending: 'pending',
    processing: 'processing',
    completed: 'completed',
    failed: 'failed'
};

export const checkoutStateDefault = {
    orderStatus: 'pending',
    currentOrderGuid: null,
    loadingShippingOptions: false,
    errorShippingOptions: false,
    updatingOrderTotals: false,
    updatingPromotion: false,
    errorUpdatingPromotion: false,
    isPunchOut: false,
    isApprovalRequired: false,
    isPaymentValidationRequired: true,
    isPORequired: false,
    addressBook: null,
    shippingOptions: [],
    paymentOptions: [],
    PONumber: '',
    promotions: [],
    selectedShippingOption: null,
    selectedShippingAddress: null,
    selectedPaymentOption: null,
    selectedBillingAddress: null
}

export const checkoutInitializer = ( customerData ) => state => {
    const {
        cart,
        currentOrderGuid = null,
        isPunchOut = false
    } = customerData;
    let additionalState = {
        selectedShippingOption: cart.shippingMethod || null,
        selectedPaymentOption: null,
        selectedBillingAddress: null
    };

    const paymentSelectionKey = getCheckoutPaymentKey( customerData );
    const savedPayment = readCheckoutPayment( paymentSelectionKey );
    if ( savedPayment ) {
        const selectedPaymentOption = state.paymentOptions.find( option => option.id === savedPayment.paymentId &&
            option.paymentMethodType === savedPayment.paymentMethodType ) || null;
        const selectedBillingAddress = selectedPaymentOption?.paymentMethodType === 'creditCard'
            ? state.addressBook.getById( selectedPaymentOption.addressId ) || selectedPaymentOption.address
            : selectedPaymentOption && state.addressBook.getAddressesByType( 'Billing' )
                .find( address => address.id === savedPayment.billingAddressId );
        additionalState = {...additionalState, selectedPaymentOption, selectedBillingAddress: selectedBillingAddress || null};
    }
    // Preserve compatibility with callers that already supply cart payment identifiers.
    else if ( notEmptyOrNil( cart.payment ) ) {
        // get current payment option from cart
        const {
            paymentId,
            billingAddressId
        } = cart.payment;
        const selectedPaymentOption = state.paymentOptions.find( ( {id} ) => id === paymentId );
        const selectedBillingAddress = state.addressBook.getById( billingAddressId );

        additionalState = {
            ...additionalState,
            selectedPaymentOption,
            selectedBillingAddress
        };
    }

    const nextState = {
        ...checkoutStateDefault, ...state, ...additionalState,
        currentOrderGuid,
        paymentSelectionKey,
        isPunchOut,
        isApprovalRequired: cart.approvalRequired === true,
        isPaymentValidationRequired: shouldRequirePaymentValidation( customerData ),
        isPORequired: shouldRequirePONumber( customerData ),
        selectedShippingAddress: state.addressBook.getSuggestedShippingAddress( cart ) || null
    };

    return nextState;
}

export const checkoutReducer = ( state, action ) => {
    let nextState = handleCheckoutAction( state, action );

    if ( action.type === 'setSelectedPaymentOption' || action.type === 'setSelectedBillingAddress' ) {
        saveCheckoutPayment( nextState.paymentSelectionKey, nextState.selectedPaymentOption, nextState.selectedBillingAddress );
    }
    else if ( action.type === 'setOrderStatus' && action.payload.status === ORDER_STATUSES.completed ) {
        saveCheckoutPayment( nextState.paymentSelectionKey, null );
    }

    switch ( action.type ) {
        case 'setSelectedShippingAddress':
        case 'setSelectedShippingOption':
        case 'setSelectedPaymentOption':
        case 'setSelectedBillingAddress':
        case 'setPONumber':
            if ( !state.hasSubmitted ) {
                return nextState;
            }

            return {
                ...nextState,
                validationErrors: validate( nextState, action.type )
            }

        default:
            return nextState;
    }
};

function handleCheckoutAction ( state, action ) {
    let nextState = {};
    switch ( action.type ) {
        case 'reinitializeCheckoutState':
            return checkoutInitializer( action.payload.customerData )( state );

        case 'validateCheckout':
            return {
                ...state,
                hasSubmitted: action.payload.hasSubmitted
            };

        case 'setOrderStatus':
            if ( !Object.values( ORDER_STATUSES ).includes( action.payload.status ) ) {
                throw Error( `Invalid order status: ${action.payload.status}` );
            }
            return {
                ...state,
                orderStatus: action.payload.status,
                submissionErrors: action.payload.errors || null
            };

        case 'setValidationErrors':
            return {
                ...state,
                validationErrors: action.payload.validationErrors
            };

        case 'setCheckoutErrors':
            return {
                ...state,
                errors: action.payload.errors
            };

        case 'setApprovalRequired':
            return {
                ...state,
                isApprovalRequired: action.payload.isApprovalRequired,
                isPaymentValidationRequired: action.payload.isPaymentValidationRequired,
                isPORequired: action.payload.isPORequired
            };

        // note - address book
        case 'setAddressBook':
            return {
                ...state,
                addressBook: action.payload.addressBook
            };

        // note - shipping options
        case 'fetchShippingOptions':
            return {
                ...state,
                loadingShippingOptions: true,
                errorShippingOptions: false
            };

        case 'errorShippingOptions':
            return {
                ...state,
                loadingShippingOptions: false,
                errorShippingOptions: true
            };

        case 'receiveShippingOptions':
            // handle edge case where shipping options are returned empty from api
            if ( action.payload.shippingOptions.length > 0 ) {
                nextState = {
                    ...state,
                    shippingOptions: action.payload.shippingOptions,
                    selectedShippingOption: getShippingOptionOrDefault( state, action.payload.shippingOptions ),
                    errorShippingOptions: false
                };
            }
            else {
                nextState = {
                    ...state,
                    shippingOptions: [],
                    selectedShippingOption: null,
                    errorShippingOptions: true
                };
            }

            return {
                ...nextState,
                loadingShippingOptions: false
            };

        case 'setSelectedShippingOption':
            dataLayer.checkout.setShippingOption( action.payload.optionId );

            return {
                ...state,
                selectedShippingOption: state.shippingOptions.find( ( {id} ) => id === action.payload.optionId )
            };

        case 'setUpdatingOrderTotals':
            return {
                ...state,
                updatingOrderTotals: action.payload.isUpdating
            };

        case 'setSelectedShippingAddress':
            return {
                ...state,
                selectedShippingAddress: action.payload.address
            };

        // note - payment methods
        case 'setPaymentOptions':
            return {
                ...state,
                paymentOptions: action.payload.paymentOptions
            };

        case 'setSelectedPaymentOption':
            dataLayer.checkout.setPaymentOption( action.payload.option?.paymentMethodType );
            CustomerService.setSelectedPaymentOption( {paymentId: action.payload.option?.id} );

            return {
                ...state,
                selectedPaymentOption: action.payload.option,
                selectedBillingAddress: action.payload.option?.paymentMethodType === 'terms' &&
                    state.selectedPaymentOption?.paymentMethodType === 'terms' &&
                    state.selectedPaymentOption?.id === action.payload.option.id
                    ? state.selectedBillingAddress
                    : action.payload.option
                        ? state.addressBook.getById( action.payload.option.addressId ) || action.payload.option.address
                        : null
            };

        case 'setSelectedBillingAddress':
            CustomerService.setSelectedPaymentOption( {billingAddressId: action.payload.address?.id} );

            return {
                ...state,
                selectedBillingAddress: action.payload.address
            };

        // note - promotions
        case 'updatePromotions':
            return {
                ...state,
                updatingPromotion: action.payload.promotionCode,
                errorUpdatingPromotion: false
            };

        case 'errorUpdatingPromotion':
            return {
                ...state,
                updatingPromotion: null,
                errorUpdatingPromotion: true
            };

        case 'receivePromotions':
            return {
                ...state,
                updatingPromotion: null,
                errorUpdatingPromotion: false,
                promotions: action.payload.promotions
            }

        // PO Number
        case 'setPONumber':
            CustomerService.setSelectedPaymentOption( {PONumber: action.payload.PONumber} );

            return {
                ...state,
                PONumber: action.payload.PONumber
            }

        default:
            throw Error( `Unknown action "${action.type}" specified for checkoutReducer` );
    }
}

export const useCheckoutActions = dispatch => ({
    reinitializeCheckoutState: ( customerData ) => {
        dispatch( {
            type: 'reinitializeCheckoutState',
            payload: {customerData}
        } );
    },

    setShippingOptionsForSelectedAddress: async ( options = {} ) => {
        const shouldIgnore = typeof options?.shouldIgnore === 'function' ? options.shouldIgnore : () => false;

        dispatch( {type: 'fetchShippingOptions'} );

        try {
            const shippingOptions = await CartService.getAvailableShippingMethods();

            if ( shouldIgnore() ) {
                return null;
            }

            dispatch( {
                type: 'receiveShippingOptions',
                payload: {shippingOptions}
            } );
        }
        catch ( err ) {
            if ( shouldIgnore() ) {
                return null;
            }

            console.error( '[Error] CartService.getAvailableShippingMethods', err );
            dispatch( {type: 'errorShippingOptions'} );
            return null;
        }
    },

    setPONumber: ( PONumber ) => {
        dispatch( {
            type: 'setPONumber',
            payload: {PONumber}
        } );
    },

    setAddressBook: ( addressBook ) => {
        dispatch( {
            type: 'setAddressBook',
            payload: {addressBook}
        } );
    },

    setPaymentOptions: ( paymentOptions ) => {
        dispatch( {
            type: 'setPaymentOptions',
            payload: {paymentOptions}
        } );
    },

    setSelectedPaymentOption: ( option ) => {
        dispatch( {
            type: 'setSelectedPaymentOption',
            payload: {option}
        } );
    },

    setSelectedBillingAddress: ( address ) => {
        dispatch( {
            type: 'setSelectedBillingAddress',
            payload: {address}
        } );
    },

    setSelectedShippingAddress: async ( address ) => {
        if ( address?.id ) {
            await CartService.updateShippingAddress( address.id );
        }

        dispatch( {
            type: 'setSelectedShippingAddress',
            payload: {address}
        } );
    },

    setSelectedShippingOption: async ( optionId ) => {
        dispatch( {
            type: 'setUpdatingOrderTotals',
            payload: {isUpdating: true}
        } );

        try {
            await CartService.updateShippingMethod( optionId );
            dispatch( {
                type: 'setSelectedShippingOption',
                payload: {optionId}
            } );
        }
        finally {
            dispatch( {
                type: 'setUpdatingOrderTotals',
                payload: {isUpdating: false}
            } );
        }
    },

    priceCart: async () => {
        dispatch( {
            type: 'setUpdatingOrderTotals',
            payload: {isUpdating: true}
        } );

        try {
            return await CartService.priceCart();
        }
        finally {
            dispatch( {
                type: 'setUpdatingOrderTotals',
                payload: {isUpdating: false}
            } );
        }
    },

    updatePromotion: ( commandName, value ) => {
        const commands = {
            add: CartService.addPromotion,
            remove: CartService.removePromotion
        };
        const promotionCode = getPromotionCode( value );

        const command = commands[commandName];
        if ( !command ) {
            return Promise.reject( new Error( `Invalid command: ${commandName}` ) );
        }

        if ( isEmptyOrNil( promotionCode ) ) {
            dispatch( {type: 'errorUpdatingPromotion'} );
            return Promise.resolve( false );
        }

        dispatch( {
            type: 'updatePromotions',
            payload: {promotionCode}
        } );
        return command( promotionCode )
            .then( payload => {
                const promotions = payload.cart?.promotions || [];

                if ( commandName === 'add' && !promotionCodeMatches( promotions, promotionCode ) ) {
                    throw new Error( `Promotion code "${promotionCode}" was not applied.` );
                }

                dispatch( {
                    type: 'receivePromotions',
                    payload: {promotions}
                } );
                return true;
            } )
            .catch( err => {
                console.error( '[Error] CartService.addPromotion', err );
                notifier.error( `Sorry, we were unable to ${commandName} promotion code "${promotionCode}".` );
                dispatch( {type: 'errorUpdatingPromotion'} );
                return false;
            } );
    },

    setCheckoutErrors: ( errors ) => {
        dispatch( {
            type: 'setCheckoutErrors',
            payload: {errors: errors}
        } );
    },

    setApprovalRequired: ( isApprovalRequired, isPORequired, isPaymentValidationRequired ) => {
        dispatch( {
            type: 'setApprovalRequired',
            payload: {isApprovalRequired, isPORequired, isPaymentValidationRequired}
        } );
    },

    setOrderStatus: ( status ) => {
        dispatch( {
            type: 'setOrderStatus',
            payload: {status}
        } );
    },

    validateCheckout: ( state ) => {
        const errors = validate( state );
        const isValid = isEmptyOrNil( errors );
        dispatch( {
            type: 'validateCheckout',
            payload: {hasSubmitted: true}
        } );

        if ( !isValid ) {
            dispatch( {
                type: 'setValidationErrors',
                payload: {validationErrors: errors}
            } );
        }

        return {
            isValid,
            validationErrors: errors
        };
    },

    processPunchOutOrder: async ( state ) => {
        dispatch( {
            type: 'setOrderStatus',
            payload: {status: ORDER_STATUSES.processing}
        } );

        try {
            await CartService.submitPunchOutOrder( state.currentOrderGuid );
        }
        catch ( errors ) {
            console.error( '[Error] CartService.submitPunchOutOrder', errors );
            dispatch( {
                type: 'setOrderStatus',
                payload: {
                    status: ORDER_STATUSES.failed,
                    errors
                }
            } );
            throw errors;
        }
    },

    processOrder: async ( state ) => {
        dispatch( {
            type: 'setOrderStatus',
            payload: {status: ORDER_STATUSES.processing}
        } );

        let order;
        try {
            order = await CartService.processOrder( {
                payment: {
                    paymentId: state.selectedPaymentOption?.id,
                    billingAddressId: state.selectedBillingAddress?.id
                },
                PONumber: state.PONumber,
                approvalRequired: state.isApprovalRequired,
                skipPaymentValidation: !state.isPaymentValidationRequired,
                shippingAddress: state.selectedShippingAddress,
                shippingMethod: state.selectedShippingOption
            } );
        }
        catch ( errors ) {
            console.error( '[Error] CartService.submitOrder', errors );
            dispatch( {
                type: 'setOrderStatus',
                payload: {
                    status: ORDER_STATUSES.failed,
                    errors
                }
            } );

            throw errors;
        }

        dispatch( {
            type: 'setOrderStatus',
            payload: {status: ORDER_STATUSES.completed}
        } );
        if ( !state.isApprovalRequired ) {
            Promise.resolve()
                .then( () => dataLayer.checkout.purchaseComplete( order.orderNumber ) )
                .catch( error => console.error( 'Error reporting completed order analytics', error ) );
        }
        return order;

    }

});

export function validate ( state, type ) {
    const errors = {};
    // has shipping address
    if ( !state.selectedShippingAddress ) {
        errors.shippingAddress = 'Shipping address is required';
    }
    // has shipping option
    if ( !state.selectedShippingOption ) {
        errors.shippingOption = 'Shipping option is required';
    }
    // has billing option
    if ( state.isPaymentValidationRequired && !state.selectedPaymentOption ) {
        errors.paymentOption = 'Payment option is required';
    }
    // has billing address IF TERMS
    if ( state.isPaymentValidationRequired &&
        state.selectedPaymentOption?.paymentMethodType ===
        'terms' &&
        !state.selectedBillingAddress ) {
        errors.billingAddress = 'Billing address is required';
    }
    // has PO number IF TERMS or isPORequired
    if ( state.isPORequired && !state.PONumber ) {
        errors.PONumber = 'PO number is required';
    }

    return errors;
}

function getShippingOptionOrDefault ( state, options ) {
    const availableOptions = options.filter( option => option.isAvailable !== false );
    const selectedOption = availableOptions.find( option => option.id === state.selectedShippingOption?.id );

    return selectedOption || availableOptions[0] || null;
}
